import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash, UserPlus, PencilSimple, Prohibit } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LIST, ROLE_LABELS, roleName, useRoles } from "@/lib/permissions";

export default function Crew() {
  const { user } = useAuth();
  const { roles, labels } = useRoles();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "fabricator",
    phone: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mobileCatalog, setMobileCatalog] = useState([]);
  const [draft, setDraft] = useState({
    name: "",
    role: "fabricator",
    password: "",
    phone: "",
    mobile_kinds: [],
    notify_mute_email: false,
    notify_mute_sms: false,
  });
  const groupName = (key) => labels[key]?.split("—")[0].trim() || roleName(key);

  const toggleDraftKind = (kind, on) =>
    setDraft((d) => ({
      ...d,
      mobile_kinds: on
        ? [...new Set([...(d.mobile_kinds || []), kind])]
        : (d.mobile_kinds || []).filter((k) => k !== kind),
    }));

  const saveEdit = async (userId) => {
    try {
      await api.put(`/users/${userId}`, {
        name: draft.name,
        role: draft.role,
        phone: draft.phone || "",
        password: draft.password || null,
        mobile_kinds: draft.mobile_kinds || [],
        notify_mute_email: !!draft.notify_mute_email,
        notify_mute_sms: !!draft.notify_mute_sms,
      });
      toast.success("Crew member updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const load = () => api.get("/users").then(({ data }) => setUsers(data));

  useEffect(() => {
    load();
    api
      .get("/settings/user-groups")
      .then(({ data }) => setMobileCatalog(data.mobile_kinds_catalog || []))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/users", form);
      toast.success(`${form.email} can now sign in`);
      setForm({
        name: "",
        email: "",
        password: "",
        role: "fabricator",
        phone: "",
      });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const remove = async (id, email) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success(`${email} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleDisabled = async (u) => {
    try {
      await api.put(`/users/${u.id}`, {
        name: u.name,
        role: u.role,
        phone: u.phone || "",
        disabled: !u.disabled,
      });
      toast.success(u.disabled ? `${u.email} can sign in again` : `${u.email} disabled`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Crew" />
      <main className="relative z-10 mx-auto max-w-4xl px-4 pb-20 pt-6">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Crew Accounts
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Add fabricator logins. Every reservation records who created and last
          updated it.
        </p>

        <form
          onSubmit={submit}
          className="mt-8 grid gap-4 sm:grid-cols-2"
          data-testid="crew-form"
        >
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Name
            </Label>
            <Input
              data-testid="crew-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-12 border-zinc-800 bg-zinc-900"
              placeholder="Dave R."
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Email
            </Label>
            <Input
              data-testid="crew-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-12 border-zinc-800 bg-zinc-900"
              placeholder="dave@shop.com"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Temporary Password
            </Label>
            <Input
              data-testid="crew-password-input"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="h-12 border-zinc-800 bg-zinc-900"
              placeholder="min 6 characters"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Mobile <span className="text-zinc-600">(for text alerts)</span>
            </Label>
            <Input
              data-testid="crew-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="h-12 border-zinc-800 bg-zinc-900"
              placeholder="(555) 123-4567"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Role
            </Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
            >
              <SelectTrigger
                data-testid="crew-role-select"
                className="h-12 border-zinc-800 bg-zinc-900"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                {roles.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`crew-role-${r}`}>
                    {groupName(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500" data-testid="crew-role-hint">
              {labels[form.role] || ROLE_LABELS[form.role]}
            </p>
          </div>
          {error && (
            <p
              data-testid="crew-error"
              className="text-sm font-medium text-red-400 sm:col-span-2"
            >
              {error}
            </p>
          )}
          <div className="sm:col-span-2">
            <Button
              data-testid="crew-add-button"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:w-auto sm:px-10"
            >
              <UserPlus size={18} weight="bold" className="mr-2" />
              {busy ? "Adding…" : "Add Crew Member"}
            </Button>
          </div>
        </form>

        <div
          className="mt-10 divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900"
          data-testid="crew-list"
        >
          {users.map((u) =>
            editing === u.id ? (
              <div
                key={u.id}
                className="space-y-3 p-4"
                data-testid={`crew-edit-row-${u.email}`}
              >
                <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                <Input
                  data-testid="crew-edit-name-input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Name"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
                <Select
                  value={draft.role}
                  onValueChange={(v) => setDraft({ ...draft, role: v })}
                >
                  <SelectTrigger
                    data-testid="crew-edit-role-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {roles.map((r) => (
                      <SelectItem key={r} value={r}>
                        {groupName(r)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="crew-edit-phone-input"
                  value={draft.phone}
                  onChange={(e) =>
                    setDraft({ ...draft, phone: e.target.value })
                  }
                  placeholder="Mobile for texts"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
                <Input
                  data-testid="crew-edit-password-input"
                  value={draft.password}
                  onChange={(e) =>
                    setDraft({ ...draft, password: e.target.value })
                  }
                  placeholder="New password (optional)"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
                <div className="flex gap-2">
                  <Button
                    data-testid="crew-save-button"
                    onClick={() => saveEdit(u.id)}
                    className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    Save
                  </Button>
                  <Button
                    data-testid="crew-cancel-edit-button"
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="h-12 border-zinc-700 bg-transparent uppercase tracking-wider hover:bg-zinc-800"
                  >
                    Cancel
                  </Button>
                </div>
                </div>
                <div className="space-y-2">
                  <label
                    data-testid="crew-edit-mute-email"
                    className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <input
                      type="checkbox"
                      checked={!!draft.notify_mute_email}
                      onChange={(e) =>
                        setDraft({ ...draft, notify_mute_email: e.target.checked })
                      }
                      className="mt-0.5 h-4 w-4 accent-[#c39e6d]"
                    />
                    <span className="text-xs text-zinc-300">
                      <span className="font-semibold text-zinc-100">
                        Mute email alerts
                      </span>
                      <br />
                      This person won't receive office alert emails.
                    </span>
                  </label>
                  <label
                    data-testid="crew-edit-mute-sms"
                    className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <input
                      type="checkbox"
                      checked={!!draft.notify_mute_sms}
                      onChange={(e) =>
                        setDraft({ ...draft, notify_mute_sms: e.target.checked })
                      }
                      className="mt-0.5 h-4 w-4 accent-[#c39e6d]"
                    />
                    <span className="text-xs text-zinc-300">
                      <span className="font-semibold text-zinc-100">
                        Mute SMS alerts
                      </span>
                      <br />
                      This person won't receive reminder or alert texts.
                    </span>
                  </label>
                </div>
                {draft.role !== "admin" && (
                  <div data-testid="crew-edit-mobile-kinds">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#d8b26a]">
                      Mobile job types (override)
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      Leave all off to follow the role default. Tick to give this
                      person their own set of job types in the mobile app.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {mobileCatalog.map((k) => {
                        const on = (draft.mobile_kinds || []).includes(k);
                        return (
                          <button
                            key={k}
                            type="button"
                            data-testid={`crew-edit-mobile-${k.toLowerCase()}`}
                            onClick={() => toggleDraftKind(k, !on)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                              on
                                ? "border-[#c39e6d]/60 bg-[#c39e6d]/15 text-[#e2c88f]"
                                : "border-zinc-700 bg-zinc-950 text-zinc-400 hover:border-zinc-600"
                            }`}
                          >
                            {k}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                key={u.id}
                className="flex items-center gap-4 px-4 py-3"
                data-testid={`crew-row-${u.email}`}
              >
                <div className="min-w-0">
                  <div className="truncate font-semibold">{u.name}</div>
                  <div className="truncate text-xs text-zinc-500">
                    {u.email}
                    {u.phone ? ` · ${u.phone}` : ""}
                  </div>
                </div>
                <span className={`ml-auto rounded-full border px-3 py-1 text-xs uppercase tracking-widest ${u.disabled ? "border-red-900 bg-red-950/40 text-red-300" : "border-zinc-700 text-zinc-300"}`}>
                  {u.disabled ? "Disabled" : groupName(u.role)}
                </span>
                <Button
                  data-testid={`crew-edit-${u.email}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditing(u.id);
                    setDraft({
                      name: u.name,
                      role: u.role,
                      password: "",
                      phone: u.phone || "",
                      mobile_kinds: u.mobile_kinds || [],
                      notify_mute_email: !!(u.notify_mute_email || u.notify_muted),
                      notify_mute_sms: !!(u.notify_mute_sms || u.notify_muted),
                    });
                  }}
                  className="text-zinc-400 hover:bg-zinc-800 hover:text-yellow-400"
                >
                  <PencilSimple size={18} weight="bold" />
                </Button>
                <Button
                  data-testid={`crew-toggle-${u.email}`}
                  variant="ghost"
                  size="icon"
                  disabled={u.email === user?.email}
                  onClick={() => toggleDisabled(u)}
                  title={u.disabled ? "Re-enable sign-in" : "Disable sign-in"}
                  className={`${u.disabled ? "text-emerald-400 hover:bg-emerald-950/40" : "text-amber-400 hover:bg-amber-950/40"} disabled:opacity-30`}
                >
                  <Prohibit size={18} weight="bold" />
                </Button>
                <Button
                  data-testid={`crew-delete-${u.email}`}
                  variant="ghost"
                  size="icon"
                  disabled={u.email === user?.email}
                  onClick={() => remove(u.id, u.email)}
                  className="text-red-400 hover:bg-red-950/40 disabled:opacity-30"
                >
                  <Trash size={18} weight="bold" />
                </Button>
              </div>
            ),
          )}
        </div>
      </main>
    </div>
  );
}
